import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useCollection } from '../data/firestore';
import { useAuth, signOut } from '../AuthContext';
import { MarkdownLite } from './Plan';
import { scrollToId } from '../components/scrollPage';

// Platform documentation, served from the Firestore `docsSections` collection
// (allowlist-gated in the rules — this page is just the viewer, same pattern
// as the business plan). Content is written by scripts/sync-docs-sections.mjs,
// whose row ids carry the hierarchy this page renders: `<key>-book` opens a
// book (body = its meta line), a later row with an empty body is a part
// divider, everything else is a section of the current part. One book is
// shown at a time; the sidebar is the map; `#<section-id>` deep links (and
// NoVA's scroll_page) select the book that owns the id, then scroll.
export default function Docs() {
  const { user } = useAuth();
  const { docs: sections, error } = useCollection('docsSections', {
    orderBy: 'order',
    dir: 'asc',
  });

  const books = useMemo(() => buildBooks(sections || []), [sections]);
  const ownerOf = useMemo(() => {
    const m = new Map();
    for (const b of books) {
      m.set(b.id, b.key);
      for (const p of b.parts) {
        m.set(p.id, b.key);
        for (const s of p.sections) { m.set(s.id, b.key); for (const f of s.figures || []) m.set(f.id, b.key); }
      }
    }
    return m;
  }, [books]);

  const [selectedKey, setSelectedKey] = useState(null);
  const [filter, setFilter] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [closedParts, setClosedParts] = useState(() => new Set());
  const pendingScroll = useRef(null);
  const selectedRef = useRef(null);
  const filterRef = useRef(null);
  useEffect(() => { selectedRef.current = selectedKey; }, [selectedKey]);

  // Hash → book (+ scroll when the target is not on screen yet). Runs on
  // load once the rows arrive and on every hashchange. When the owner book
  // is already shown the browser's own anchor jump does the scrolling
  // (scroll-margin-top keeps it under the header).
  useEffect(() => {
    if (!books.length) return undefined;
    const apply = (initial) => {
      const id = window.location.hash.slice(1);
      const key = ownerOf.get(id);
      if (!key) { if (!selectedRef.current) setSelectedKey(books[0].key); return; }
      if (initial || key !== selectedRef.current) pendingScroll.current = id;
      setSelectedKey(key);
    };
    apply(true);
    const onHash = () => apply(false);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [books, ownerOf]);

  // After the selected book renders, finish a pending deep-link scroll.
  useEffect(() => {
    const id = pendingScroll.current;
    if (!id || !selectedKey) return;
    pendingScroll.current = null;
    if (id.endsWith('-book')) { window.scrollTo(0, 0); return; }
    scrollToId(id);
  }, [selectedKey, books]);

  const book = books.find((b) => b.key === selectedKey) || books[0] || null;

  // Active section = the top-most section heading inside the reading band.
  useEffect(() => {
    if (!book) return undefined;
    const els = book.parts.flatMap((p) => p.sections).map((s) => document.getElementById(s.id)).filter(Boolean);
    if (!els.length) return undefined;
    const visible = new Map();
    const obs = new IntersectionObserver((entries) => {
      for (const e of entries) { if (e.isIntersecting) visible.set(e.target.id, e.boundingClientRect.top); else visible.delete(e.target.id); }
      if (!visible.size) return;
      const top = [...visible.entries()].sort((a, b) => a[1] - b[1])[0][0];
      setActiveId(top);
    }, { rootMargin: '-80px 0px -60% 0px', threshold: 0 });
    for (const el of els) obs.observe(el);
    return () => obs.disconnect();
  }, [book]);

  // `/` focuses the filter from anywhere on the page that is not a field.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      e.preventDefault();
      filterRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (error) {
    const denied = /permission/i.test(error);
    return (
      <div className="cs-page">
        <div className="cs-empty" style={{ maxWidth: 520 }}>
          {denied ? (
            <>
              <p style={{ marginTop: 0 }}>
                <strong>{user?.email}</strong> isn't approved for the platform
                docs. Access is invite-only — contact Nventr to have your email
                added.
              </p>
              <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => signOut()}>
                Sign out
              </button>
            </>
          ) : (
            <>Couldn't load the docs: {error}</>
          )}
        </div>
      </div>
    );
  }
  if (sections === null || !book) {
    return (
      <div className="cs-page">
        <div className="cs-empty">Loading the docs…</div>
      </div>
    );
  }

  const goTo = (id) => {
    if (window.location.hash === `#${id}`) {
      // Same hash twice fires no hashchange; scroll by hand.
      if (ownerOf.get(id) === book.key) scrollToId(id); else pendingScroll.current = id;
      setSelectedKey(ownerOf.get(id) || book.key);
      return;
    }
    window.location.hash = id;
  };
  const togglePart = (id) => setClosedParts((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <div className="cs-page cs-docs-layout">
      <DocsSidebar
        books={books}
        book={book}
        activeId={activeId}
        filter={filter}
        setFilter={setFilter}
        filterRef={filterRef}
        closedParts={closedParts}
        togglePart={togglePart}
        goTo={goTo}
      />

      <main className="cs-plan cs-docs-main">
        <h1 id={book.id}>{book.title}</h1>
        {book.meta ? <p className="cs-store-note">{book.meta}</p> : null}
        {book.parts.map((p) => (
          <React.Fragment key={p.id}>
            {p.title ? <h2 id={p.id} className="cs-docs-part">{p.title}</h2> : null}
            {p.sections.map((s) => (
              <section key={s.id} id={s.id} className="cs-plan-section">
                <h2>{s.title}</h2>
                <SectionBody body={s.body} figures={s.figures} />
              </section>
            ))}
          </React.Fragment>
        ))}
      </main>
    </div>
  );
}

// Rows → books → parts → sections. A book without part dividers gets one
// unnamed part so the sidebar lists its sections flat.
function buildBooks(rows) {
  const books = [];
  let book = null;
  let part = null;
  for (const r of rows) {
    if (r.id.endsWith('-book')) {
      book = { key: r.id.slice(0, -5), id: r.id, title: r.title, meta: r.body || '', parts: [] };
      part = null;
      books.push(book);
      continue;
    }
    if (!book) continue;
    if (!r.body) { part = { id: r.id, title: r.title, sections: [] }; book.parts.push(part); continue; }
    if (!part) { part = { id: `${book.key}-front`, title: '', sections: [] }; book.parts.push(part); }
    part.sections.push(r);
  }
  return books;
}

function DocsSidebar({ books, book, activeId, filter, setFilter, filterRef, closedParts, togglePart, goTo }) {
  const q = filter.trim().toLowerCase();
  const [cursor, setCursor] = useState(0);
  const hits = useMemo(() => {
    if (!q) return [];
    const out = [];
    for (const b of books) for (const p of b.parts) for (const s of p.sections) {
      if (s.title.toLowerCase().includes(q) || (p.title && p.title.toLowerCase().includes(q))) out.push({ book: b, section: s });
    }
    return out;
  }, [books, q]);
  useEffect(() => { setCursor(0); }, [q]);

  const onKeyDown = (e) => {
    if (!hits.length) { if (e.key === 'Escape') setFilter(''); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(hits.length - 1, c + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); goTo(hits[cursor].section.id); setFilter(''); }
    else if (e.key === 'Escape') setFilter('');
  };

  return (
    <aside className="cs-docs-side" data-nova-note="Book picker and section list; the main column shows one book at a time. Type in the filter to find a section by title across all books.">
      <input
        ref={filterRef}
        className="cs-input cs-docs-filter"
        type="search"
        placeholder="Find a section…  ( / )"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        onKeyDown={onKeyDown}
        aria-label="Find a section"
      />

      {q ? (
        <div className="cs-docs-hits">
          {hits.length === 0 ? <div className="cs-docs-side-empty">No section titled like “{filter}”.</div> : null}
          {groupHits(hits).map((g) => (
            <div key={g.book.key} className="cs-docs-side-group">
              <div className="cs-docs-side-booklabel">{g.book.title}</div>
              {g.items.map(({ section, index }) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className={`cs-docs-side-link ${index === cursor ? 'is-active' : ''}`}
                  onClick={(e) => { e.preventDefault(); goTo(section.id); setFilter(''); }}
                >
                  {section.title}
                </a>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="cs-docs-books" role="tablist" aria-label="Books">
            {books.map((b) => (
              <button
                key={b.key}
                type="button"
                role="tab"
                aria-selected={b.key === book.key}
                className={`cs-docs-book ${b.key === book.key ? 'is-active' : ''}`}
                onClick={() => goTo(b.id)}
                title={b.meta}
              >
                <span className="cs-docs-book-title">{b.title}</span>
                <span className="cs-docs-book-count">{b.parts.reduce((n, p) => n + p.sections.length, 0)}</span>
              </button>
            ))}
          </div>

          <nav className="cs-docs-tree" aria-label={`${book.title} contents`}>
            {book.parts.map((p) => {
              const closed = closedParts.has(p.id);
              const holdsActive = p.sections.some((s) => s.id === activeId);
              return (
                <div key={p.id} className={`cs-docs-side-group ${holdsActive ? 'has-active' : ''}`}>
                  {p.title ? (
                    <button type="button" className="cs-docs-side-part" aria-expanded={!closed} onClick={() => togglePart(p.id)}>
                      <span className="cs-docs-side-caret">{closed ? '▸' : '▾'}</span>
                      {p.title}
                    </button>
                  ) : null}
                  {closed ? null : p.sections.map((s) => (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      className={`cs-docs-side-link ${s.id === activeId ? 'is-active' : ''}`}
                      aria-current={s.id === activeId ? 'location' : undefined}
                      onClick={(e) => { e.preventDefault(); goTo(s.id); }}
                    >
                      {s.title}
                    </a>
                  ))}
                </div>
              );
            })}
          </nav>
        </>
      )}
    </aside>
  );
}

function groupHits(hits) {
  const groups = [];
  hits.forEach((h, index) => {
    let g = groups[groups.length - 1];
    if (!g || g.book.key !== h.book.key) { g = { book: h.book, items: [] }; groups.push(g); }
    g.items.push({ section: h.section, index });
  });
  return groups;
}

// A section body is MarkdownLite prose with `[figure:<id>]` marker lines
// where the master had a picture (sync-docs-sections.mjs); each marker
// renders the figure from the section's `figures` list — the same picture
// as the printed edition, with its printed page, as a <figure id> NoVA can
// scroll to by name or page (pitch_step → section id).
const MARKER = /^\[figure:([\w-]+)\]$/;
function SectionBody({ body, figures }) {
  const byId = new Map((figures || []).map((f) => [f.id, f]));
  const parts = [];
  let buf = [];
  const flush = () => { if (buf.length) { parts.push({ text: buf.join('\n') }); buf = []; } };
  for (const line of String(body || '').split('\n')) {
    const m = MARKER.exec(line.trim());
    if (m) { flush(); if (byId.has(m[1])) parts.push({ figure: byId.get(m[1]) }); }
    else buf.push(line);
  }
  flush();
  return parts.map((p, i) => (p.figure ? (
    <figure key={p.figure.id} id={p.figure.id} className="cs-guide-figure cs-docs-figure" data-nova-note={`Figure${p.figure.page ? ` (print p. ${p.figure.page})` : ''}: ${p.figure.caption}`}>
      <img src={p.figure.src} alt={p.figure.caption} loading="lazy" />
      <figcaption>{p.figure.caption}{p.figure.page ? <span className="cs-docs-figure-page"> · print p. {p.figure.page}</span> : null}</figcaption>
    </figure>
  ) : <MarkdownLite key={i} text={p.text} />));
}
